/**
 * Shared service for monthly performance data and utility cost calculations
 * This ensures both Monthly Performance Table and Financial Analysis use the same data source
 */

import { getAuth } from 'firebase/auth';
import { TARIFF_DEFAULTS } from '../config/defaults';
import { API_BASE_URL } from './api';

class MonthlyPerformanceService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  /**
   * Get tariff to use for calculations
   * Returns an object with the tariff and its source for transparency
   * @returns {{ tariff: object, source: 'user_selected' | 'results_embedded' | 'generic_fallback', warning?: string }}
   */
  _getTariffToUse(selectedTariff, results) {
    // Priority 1: User-selected tariff from context
    if (selectedTariff) {
      return {
        tariff: selectedTariff,
        source: 'user_selected',
        warning: null
      };
    }

    // Priority 2: Tariff embedded in REopt/Vortx results
    const electricTariff = results?.inputs?.ElectricTariff;

    // DEBUG: Trace tariff detection
    console.log('MonthlyPerformanceService: Checking for tariff', {
      selectedTariff: !!selectedTariff,
      hasResultsTariff: !!electricTariff,
      resultsTariff: electricTariff
    });

    // Check multiple indicators that a structured tariff was used
    const hasStructuredTariff = electricTariff && (
      electricTariff.urdb_rate_name ||
      electricTariff.urdb_utility_name ||
      (electricTariff.energyratestructure && electricTariff.energyratestructure.length > 0)
    );
    if (hasStructuredTariff) {
      return {
        tariff: electricTariff,
        source: 'results_embedded',
        warning: null
      };
    }

    // Check for blended rates (manual or embedded)
    const hasBlendedRates = electricTariff && (
      electricTariff.blended_annual_energy_rate !== undefined ||
      electricTariff.blended_annual_rates_us_dollars_per_kwh !== undefined ||
      (electricTariff.rate_data?.blendedEnergyRate !== undefined)
    );
    if (hasBlendedRates) {
      return {
        tariff: electricTariff,
        source: 'results_embedded',
        warning: null
      };
    }

    // Priority 3: No valid tariff found
    return {
      tariff: null,
      source: 'missing',
      warning: 'No tariff selected. Please select a tariff to view financial results.'
    };
  }

  /**
   * Create cache key for request
   */
  _getCacheKey(results, tariff) {
    const resultsHash = results ? JSON.stringify({
      runId: results.run_uuid,
      loadHash: results.outputs?.ElectricLoad?.load_series_kw?.length || 0
    }) : 'no-results';

    const tariffHash = tariff ? (tariff.id || tariff.label || 'generic') : 'no-tariff';

    return `${resultsHash}-${tariffHash}`;
  }

  /**
   * Fetch monthly performance data from backend
   * @returns {Promise<{ ..., tariff_source: string, tariff_warning: string|null }>}
   */
  async fetchMonthlyPerformance(results, selectedTariff, options = {}) {
    const { forceRefresh = false, calculationMethod = 'reopt', caDerConfig = null } = options;

    if (!results) {
      return null;
    }

    // Get tariff with source tracking
    const { tariff: tariffToUse, source: tariffSource, warning: tariffWarning } = this._getTariffToUse(selectedTariff, results);

    console.log('🔍 MonthlyPerformanceService tariff resolution:', {
      selectedTariffPassed: !!selectedTariff,
      tariffToUse: !!tariffToUse,
      tariffSource,
      tariffWarning
    });

    // If no tariff is found, return "missing" status immediately (don't fetch)
    if (!tariffToUse) {
      return {
        tariff_data_source: 'missing',
        tariff_source: 'missing',
        tariff_warning: tariffWarning,
        asset_performance: [],
        bau_monthly_costs: [],
        project_monthly_costs: [],
        monthly_savings: []
      };
    }

    // Cache key must include ALL CA DER flags that affect calculation
    const derUtility = caDerConfig?.calculated?.effective_utility || caDerConfig?.detected_utility || null;
    if (caDerConfig && !derUtility) {
      console.warn('MonthlyPerformanceService: CA DER config present but no utility detected (both effective_utility and detected_utility are missing). CA DER charge calculations will be inaccurate.', caDerConfig);
    }
    const derConfigHash = caDerConfig ? JSON.stringify({
      standby: caDerConfig?.calculated?.final_standby_apply || false,
      departing: caDerConfig?.calculated?.final_departing_load_apply || false,
      utility: derUtility
    }) : 'no-der';
    const cacheKey = this._getCacheKey(results, tariffToUse) + `-${calculationMethod}-${derConfigHash}`;

    // Return cached data if available and not forcing refresh
    if (!forceRefresh && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // If there's already a pending request for this key, return that promise
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const requestPromise = this._performFetch(results, tariffToUse, calculationMethod, caDerConfig);

    // Store pending request
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const data = await requestPromise;

      // Add tariff source info to the response for UI transparency
      const enrichedData = {
        ...data,
        tariff_source: tariffSource,
        tariff_warning: tariffWarning,
        _usingGenericFallback: tariffSource === 'generic_fallback'
      };

      // Cache the successful result
      this.cache.set(cacheKey, enrichedData);

      return enrichedData;
    } catch (error) {
      console.error('Failed to fetch monthly performance data:', error);
      throw error;
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Perform the actual fetch request
   */
  async _performFetch(results, tariff, calculationMethod = 'reopt', caDerConfig = null) {
    console.log(`Fetching monthly performance data with tariff:`, tariff, `calculation method: ${calculationMethod}`);
    console.log(`CA DER Config:`, caDerConfig);

    // Transform tariff if it has rate_data structure (manual blended rates)
    let tariffToSend = tariff;
    if (tariff?.rate_data) {
      tariffToSend = {
        ...tariff,
        // Backend expects these fields at top level
        blended_annual_energy_rate: tariff.rate_data.blendedEnergyRate,
        blended_annual_demand_rate: tariff.rate_data.blendedDemandRate || 0
      };
    }

    // Build scenario object from results inputs and CA DER config
    const scenario = {
      ...(results?.inputs || {}),
      // Include CA DER config under the key expected by the backend
      ca_der_charges: caDerConfig
    };

    const headers = { 'Content-Type': 'application/json' };
    const auth = getAuth();
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/site_dna/monthly_performance`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        reopt_results: results,
        scenario: scenario,
        tariff_data: tariffToSend,
        calculation_method: calculationMethod  // 'reopt' or 'vortx'
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // Log validation results for debugging
    if (data.validation) {
      console.log('🔍 UTILITY COST VALIDATION RESULTS:');
      console.log(`Overall Status: ${data.validation.overall_validation}`);

      if (data.validation.bau_comparison?.available) {
        const bau = data.validation.bau_comparison;
        console.log(`BAU Costs: Our $${bau.our_value?.toLocaleString()} vs REopt $${bau.reopt_value?.toLocaleString()} (${bau.difference_pct?.toFixed(1)}% diff) - ${bau.match ? '✅ MATCH' : '❌ DIFFERS'}`);
      }

      if (data.validation.project_comparison?.available) {
        const project = data.validation.project_comparison;
        console.log(`Project Costs: Our $${project.our_value?.toLocaleString()} vs REopt $${project.reopt_value?.toLocaleString()} (${project.difference_pct?.toFixed(1)}% diff) - ${project.match ? '✅ MATCH' : '❌ DIFFERS'}`);
      }

      if (data.validation.savings_comparison?.available) {
        const savings = data.validation.savings_comparison;
        console.log(`Annual Savings: Our $${savings.our_value?.toLocaleString()} vs REopt $${savings.reopt_value?.toLocaleString()} (${savings.difference_pct?.toFixed(1)}% diff) - ${savings.match ? '✅ MATCH' : '❌ DIFFERS'}`);
      }
    }

    return data;
  }

  /**
   * Clear cache (useful when tariff selection changes)
   */
  clearCache(cacheKey = null) {
    if (cacheKey) {
      this.cache.delete(cacheKey);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cached data without making a request
   */
  getCachedData(results, selectedTariff) {
    const { tariff: tariffToUse } = this._getTariffToUse(selectedTariff, results);
    const cacheKey = this._getCacheKey(results, tariffToUse);
    return this.cache.get(cacheKey) || null;
  }
}

// Create singleton instance
const monthlyPerformanceService = new MonthlyPerformanceService();

export default monthlyPerformanceService;